import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@material-ui/core";

import HistoryRoundedIcon from "@material-ui/icons/HistoryRounded";
import FilterListRoundedIcon from "@material-ui/icons/FilterListRounded";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import PersonRoundedIcon from "@material-ui/icons/PersonRounded";
import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
// MUI v4 has no Login glyph; ExitToAppRounded is the conventional
// "enter the app" arrow in this codebase. Aliased so the impersonate
// audit row keeps a semantic icon without an icon-package upgrade.
import LoginRoundedIcon from "@material-ui/icons/ExitToAppRounded";
import BlockRoundedIcon from "@material-ui/icons/BlockRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import SmartToyRoundedIcon from "@material-ui/icons/EmojiObjects";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

/**
 * AdminAuditLog — platform-wide AdminAuditLog viewer for super-admins.
 *
 * Shows every action taken by a platform operator against any tenant:
 * impersonate, ai_enable/disable, suspend/reactivate, etc. Bounded by
 * the AdminAuditLog table — this is an audit trail, not a tenant-data
 * surface. NO customer messages/contacts/billing data.
 *
 * Filters: action type, acting admin, date range. Pagination at 50/page.
 */

// === Design tokens (CRM cardStackMode v3) ===
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surface = isDark ? "#13181a" : "#ffffff";

  // Action dot color coding per brief
  const dotCreateCol = "#25d366"; // green
  const dotUpdateCol = isDark ? "#60a5fa" : "#2563eb"; // blue
  const dotDeleteCol = isDark ? "#fca5a5" : "#dc2626"; // red
  const dotLoginCol = isDark ? "#c4b5fd" : "#7c3aed"; // purple

  return {
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: SP.md,
    },

    // ── Header
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: SP.sm,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.sm,
      },
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
    },
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: accentSoft,
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: accent,
      },
    },
    title: {
      fontSize: 17,
      fontWeight: 700,
      color: fg,
      lineHeight: 1.2,
      letterSpacing: "-0.005em",
    },
    subtitle: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginTop: 4,
    },
    refreshBtn: {
      background: surface,
      border: `1px solid ${hair}`,
      color: fg,
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 600,
      "&:hover": { background: accentSoft, color: accent, borderColor: accent },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        height: TT.primary,
        background: accent,
        color: "#ffffff",
        border: "none",
        fontSize: 14,
        fontWeight: 700,
        "&:hover": {
          background: isDark ? "#26c069" : "#0e6c61",
          color: "#ffffff",
          borderColor: "transparent",
        },
      },
    },

    // ── Filter bar (hairline bottom border, WhatsApp green active states)
    filterBar: {
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: 16,
      padding: SP.md,
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
      gap: SP.sm,
      alignItems: "center",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset"
        : "0 1px 0 rgba(15,22,20,0.02), 0 2px 6px rgba(15,22,20,0.03)",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr 1fr",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
        padding: SP.sm,
        gap: SP.sm,
        borderRadius: 14,
        borderTop: "none",
        borderInlineStart: "none",
        borderInlineEnd: "none",
        borderBottom: `1px solid ${hair}`,
      },
    },
    field: {
      "& .MuiInputBase-root": { fontSize: 13 },
      "& .MuiInputLabel-root": { fontSize: 13 },
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: accent,
          borderWidth: 1,
        },
        // Active filter (non-empty selection) gets WhatsApp green accent
        "&.Mui-focused": {
          background: accentSoft,
        },
      },
      "& .MuiInputLabel-root.Mui-focused": { color: accent },
      // xs: 16px input font defeats iOS Safari zoom-on-focus
      [theme.breakpoints.down("xs")]: {
        "& .MuiInputBase-input": { fontSize: 16, minHeight: TT.min - 16 },
        "& .MuiSelect-select": { minHeight: TT.min - 16, paddingTop: 12, paddingBottom: 12 },
      },
    },
    fieldActive: {
      // Visual chip-like state when a filter has a non-empty value
      "& .MuiOutlinedInput-root": {
        background: accentSoft,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: accent,
        },
      },
      "& .MuiInputLabel-root": { color: accent },
    },
    resetBtn: {
      borderColor: hair,
      color: fgMuted,
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 600,
      "&:hover": { background: accentSoft, color: accent, borderColor: accent },
      [theme.breakpoints.down("xs")]: {
        height: TT.min,
        width: "100%",
      },
    },

    // ── Card stack (replaces flat table) — used desktop + mobile
    cardStack: {
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
        : "0 14px 36px rgba(15,22,20,0.06), 0 2px 6px rgba(15,22,20,0.04)",
      [theme.breakpoints.down("xs")]: {
        background: "transparent",
        border: "none",
        boxShadow: "none",
        borderRadius: 0,
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
        gap: SP.xs, // 8px gap between cards on mobile per brief
      },
    },

    // Each audit log entry as a card row
    auditCard: {
      display: "grid",
      // Widen timestamp 140→168 and IP 90→112 so accent-meta + monospace
      // tokens fit without per-letter wrap from 0.22em letter-spacing.
      gridTemplateColumns: "16px 168px minmax(0, 1fr) 160px minmax(0, 1fr) 112px",
      columnGap: SP.sm,
      padding: "12px 16px",
      borderBottom: `1px solid ${hair}`,
      alignItems: "start",
      transition: "background 0.14s ease",
      // Every direct grid child gets minWidth:0 so 1fr columns can shrink
      // below content-min instead of crushing the fixed-width neighbours.
      "& > *": { minWidth: 0 },
      "&:last-child": { borderBottom: "none" },
      "&:hover": { background: accentSoft },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "16px minmax(0, 1fr) minmax(0, 1fr)",
        rowGap: SP.xs,
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense card: 12px padding, 8px radius, hairline border, near-flat shadow
        display: "block",
        position: "relative",
        padding: "12px",
        background: surface,
        border: `1px solid ${hair}`,
        borderRadius: 8,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.4)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        marginBottom: 8,
        opacity: 0,
        animation: "$auditCardIn 0.42s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        animationDelay: "calc(var(--i, 0) * 40ms)",
        "&:active": { transform: "scale(0.99)" },
        "&:hover": { background: surface },
        // Status dot rendered top-left via header row; visual cue for at-a-glance health
      },
    },

    "@keyframes auditCardIn": {
      "0%": { opacity: 0, transform: "translateY(8px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },

    // Card header row (mobile only) — top line with dot + timestamp meta
    cardHeaderRow: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        alignItems: "center",
        gap: SP.xs,
        marginBottom: SP.xs,
        paddingBottom: SP.xs,
        borderBottom: `1px solid ${hair}`,
      },
    },

    // Action color dot — 10px desktop, 7px xs status dot for at-a-glance health
    actionDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      marginTop: 6,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
      [theme.breakpoints.down("xs")]: {
        width: 7,
        height: 7,
        marginTop: 0,
        boxShadow: "none",
      },
    },
    dotCreate: { background: dotCreateCol },
    dotUpdate: { background: dotUpdateCol },
    dotDelete: { background: dotDeleteCol },
    dotLogin: { background: dotLoginCol },
    dotDefault: { background: fgMuted },

    // Timestamp — 0.22em accent meta uppercase 10.5px per brief
    whenMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      fontFeatureSettings: '"tnum" 1',
      lineHeight: 1.4,
      // Desktop: keep the whole timestamp on one line. Without this the
      // 0.22em letter-spacing + uppercase made every gap a wrap candidate
      // inside the fixed timestamp column, producing per-letter stacks.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta row: 11/14 weight 400 fgMuted, wrap not truncate
        flex: 1,
        minWidth: 0,
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
        letterSpacing: "0em",
        textTransform: "none",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },

    // Action description — Roboto medium 14
    actionDescription: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.4,
      color: fg,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token: 14/18 weight 500 fg, wrap not truncate
        fontSize: 14,
        lineHeight: "18px",
        fontWeight: 500,
        color: fg,
        letterSpacing: "-0.005em",
        marginBottom: 6,
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },

    actor: {
      fontSize: 13,
      color: fg,
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      // Long localized actor names truncate instead of pushing the pill column.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& svg": { fontSize: 14, color: fgMuted, flexShrink: 0 },
    },
    actorEmail: {
      fontSize: 11,
      color: fgMuted,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // Action pill — neutralised, accent border on active
    actionPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.2,
      border: `1px solid ${hair}`,
      // Keep multi-word action labels on a single line so the pill stays a pill.
      whiteSpace: "nowrap",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& svg": { fontSize: 13, flexShrink: 0 },
    },
    actionImpersonate: {
      background: isDark ? "rgba(124,58,237,0.12)" : "rgba(124,58,237,0.08)",
      color: isDark ? "#c4b5fd" : "#6d28d9",
      borderColor: isDark ? "rgba(124,58,237,0.28)" : "rgba(124,58,237,0.20)",
    },
    actionAiEnable: {
      background: accentSoft,
      color: accent,
      borderColor: isDark ? "rgba(46,227,122,0.28)" : "rgba(18,140,126,0.20)",
    },
    actionAiDisable: {
      background: isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.10)",
      color: fgMuted,
      borderColor: hair,
    },
    actionSuspend: {
      background: isDark ? "rgba(220,38,38,0.14)" : "rgba(220,38,38,0.08)",
      color: isDark ? "#fca5a5" : "#b91c1c",
      borderColor: isDark ? "rgba(220,38,38,0.28)" : "rgba(220,38,38,0.20)",
    },
    actionReactivate: {
      background: accentSoft,
      color: accent,
      borderColor: isDark ? "rgba(46,227,122,0.28)" : "rgba(18,140,126,0.20)",
    },
    actionDefault: {
      background: isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.10)",
      color: fgMuted,
      borderColor: hair,
    },

    target: {
      fontSize: 13,
      color: fg,
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& svg": { fontSize: 14, color: fgMuted, flexShrink: 0 },
    },
    targetMeta: {
      fontSize: 11,
      color: fgMuted,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // Diff / context block — fgMuted Roboto 12, collapsible visual treatment
    diffBlock: {
      marginTop: SP.xs,
      padding: "8px 10px",
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,22,20,0.025)",
      borderInlineStart: `2px solid ${hair}`,
      borderRadius: 6,
      fontSize: 12,
      lineHeight: 1.5,
      color: fgMuted,
      fontFamily: "Roboto, sans-serif",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    },

    ip: {
      fontSize: 11,
      color: fgMuted,
      fontVariantNumeric: "tabular-nums",
      fontFamily: "monospace",
      // IPv6 + x-forwarded-for chains would otherwise wrap per character
      // inside the fixed IP column. Single-line + ellipsis instead.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // Mobile-only labelled body rows
    mobileFieldRow: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: SP.xs,
      },
    },
    mobileFieldLabel: {
      // Linear-dense KV label: 12/14 weight 500 fgMuted (sentence-case, no uppercase)
      fontSize: 12,
      lineHeight: "14px",
      fontWeight: 500,
      color: fgMuted,
      letterSpacing: "0.01em",
      textTransform: "none",
    },
    mobileFieldValue: {
      // Linear-dense KV value: 12/16 weight 400 fg, wrap not truncate
      fontSize: 12,
      lineHeight: "16px",
      fontWeight: 400,
      color: fg,
      whiteSpace: "normal",
      wordBreak: "break-word",
    },

    // Desktop-only cell wrappers (hide on xs)
    desktopOnly: {
      [theme.breakpoints.down("xs")]: { display: "none" },
    },

    // Pagination
    pagination: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "4px 4px",
      fontSize: 12.5,
      color: fgMuted,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        gap: SP.sm,
        alignItems: "stretch",
        textAlign: "center",
      },
    },
    paginationBtns: {
      display: "flex",
      gap: SP.xs,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        "& > *": { flex: 1 },
      },
    },
    pageBtn: {
      fontSize: 12,
      fontWeight: 600,
      borderColor: hair,
      borderRadius: 10,
      color: fg,
      textTransform: "none",
      "&:hover": { background: accentSoft, color: accent, borderColor: accent },
      "&:disabled": { opacity: 0.4 },
      [theme.breakpoints.down("xs")]: {
        height: TT.min,
      },
    },

    loadingWrap: {
      display: "grid",
      placeItems: "center",
      minHeight: 280,
    },

    // Empty state — pattern reference (64px num, accent meta, muted desc)
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 14,
      padding: "56px 24px 32px",
      textAlign: "center",
      "& .emptyNum": {
        fontSize: 64,
        fontWeight: 400,
        letterSpacing: "-0.02em",
        color: accent,
        lineHeight: 1,
        opacity: 0.85,
        margin: 0,
      },
      "& .emptyMeta": {
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: accent,
      },
      "& .emptyDesc": {
        fontSize: 14,
        lineHeight: 1.5,
        color: fgMuted,
        maxWidth: 280,
        margin: 0,
      },
    },
  };
});

const ACTION_ICONS = {
  impersonate: LoginRoundedIcon,
  ai_enable: SmartToyRoundedIcon,
  ai_disable: SmartToyRoundedIcon,
  suspend_tenant: BlockRoundedIcon,
  reactivate_tenant: CheckCircleRoundedIcon,
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const AdminAuditLog = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    action: "",
    adminUserId: "",
    since: "",
    until: "",
  });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filters.action) params.action = filters.action;
      if (filters.adminUserId) params.adminUserId = filters.adminUserId;
      if (filters.since) params.since = filters.since;
      if (filters.until) params.until = filters.until;

      const { data: payload } = await api.get("/api/admin/audit-log", { params });
      setData(payload);
    } catch (e) {
      setData(null);
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const onFilter = (k, v) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [k]: v }));
  };

  const reset = () => {
    setPage(1);
    setFilters({ action: "", adminUserId: "", since: "", until: "" });
  };

  const actionClassName = useCallback(
    (a) => {
      if (a === "impersonate") return classes.actionImpersonate;
      if (a === "ai_enable") return classes.actionAiEnable;
      if (a === "ai_disable") return classes.actionAiDisable;
      if (a === "suspend_tenant") return classes.actionSuspend;
      if (a === "reactivate_tenant") return classes.actionReactivate;
      return classes.actionDefault;
    },
    [classes]
  );

  // Map action verbs to color-coded dot per brief
  //   create=green, update=blue, delete=red, login=purple
  const dotClassName = useCallback(
    (a) => {
      if (!a) return classes.dotDefault;
      if (a === "impersonate") return classes.dotLogin;
      if (a.startsWith("ai_enable") || a.startsWith("create") || a === "reactivate_tenant")
        return classes.dotCreate;
      if (a.startsWith("update") || a === "ai_disable") return classes.dotUpdate;
      if (a.startsWith("delete") || a === "suspend_tenant") return classes.dotDelete;
      if (a.startsWith("login") || a.startsWith("signin")) return classes.dotLogin;
      return classes.dotDefault;
    },
    [classes]
  );

  // Mark a filter field as "active" when its value is non-empty so it picks
  // up the WhatsApp-green chip treatment.
  const fieldClass = (value) =>
    `${classes.field}${value ? ` ${classes.fieldActive}` : ""}`;

  const pagination = useMemo(() => {
    if (!data) return null;
    const start = (data.page - 1) * data.limit + 1;
    const end = Math.min(data.page * data.limit, data.total);
    return { start, end, total: data.total, totalPages: data.totalPages };
  }, [data]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <HistoryRoundedIcon />
          </span>
          <Box>
            <Typography className={classes.title} component="div">
              {i18n.t("admin.audit.title", { defaultValue: "Audit log" })}
            </Typography>
            <Typography className={classes.subtitle} component="div">
              {i18n.t("admin.audit.subtitle", {
                defaultValue: "Every platform-operator action across all tenants",
              })}
            </Typography>
          </Box>
        </Box>
        <Button
          className={classes.refreshBtn}
          onClick={load}
          startIcon={<RefreshRoundedIcon />}
          size="small"
        >
          {i18n.t("admin.audit.refresh", { defaultValue: "Refresh" })}
        </Button>
      </Box>

      <Box className={classes.filterBar}>
        <FormControl variant="outlined" size="small" className={fieldClass(filters.action)}>
          <InputLabel>
            {i18n.t("admin.audit.filter.action", { defaultValue: "Action" })}
          </InputLabel>
          <Select
            label={i18n.t("admin.audit.filter.action", { defaultValue: "Action" })}
            value={filters.action}
            onChange={(e) => onFilter("action", e.target.value)}
          >
            <MenuItem value="">
              {i18n.t("admin.audit.filter.allActions", { defaultValue: "All actions" })}
            </MenuItem>
            {(data?.filters?.actions || []).map((a) => (
              <MenuItem key={a} value={a}>
                {i18n.t(`admin.health.action.${a}`, { defaultValue: a })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={fieldClass(filters.adminUserId)}>
          <InputLabel>
            {i18n.t("admin.audit.filter.admin", { defaultValue: "Admin" })}
          </InputLabel>
          <Select
            label={i18n.t("admin.audit.filter.admin", { defaultValue: "Admin" })}
            value={filters.adminUserId}
            onChange={(e) => onFilter("adminUserId", e.target.value)}
          >
            <MenuItem value="">
              {i18n.t("admin.audit.filter.allAdmins", { defaultValue: "All admins" })}
            </MenuItem>
            {(data?.filters?.admins || []).map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={i18n.t("admin.audit.filter.since", { defaultValue: "From" })}
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          className={fieldClass(filters.since)}
          value={filters.since}
          inputProps={{ max: filters.until || todayISO() }}
          onChange={(e) => onFilter("since", e.target.value)}
        />
        <TextField
          label={i18n.t("admin.audit.filter.until", { defaultValue: "To" })}
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          className={fieldClass(filters.until)}
          value={filters.until}
          inputProps={{ min: filters.since, max: todayISO() }}
          onChange={(e) => onFilter("until", e.target.value)}
        />

        <Button
          className={classes.resetBtn}
          variant="outlined"
          size="small"
          onClick={reset}
          startIcon={<FilterListRoundedIcon />}
        >
          {i18n.t("admin.audit.reset", { defaultValue: "Reset" })}
        </Button>
      </Box>

      <Box className={classes.cardStack}>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={24} style={{ color: "#128c7e" }} />
          </Box>
        ) : !data || data.rows.length === 0 ? (
          <Box className={classes.emptyState}>
            <div className="emptyNum">0</div>
            <div className="emptyMeta">
              {i18n.t("admin.audit.emptyMeta", { defaultValue: "NO AUDIT ENTRIES" })}
            </div>
            <div className="emptyDesc">
              {i18n.t("admin.audit.empty", {
                defaultValue: "No audit entries match your filters.",
              })}
            </div>
          </Box>
        ) : (
          data.rows.map((row, i) => {
            const Icon = ACTION_ICONS[row.action] || HistoryRoundedIcon;
            const actionLabel = i18n.t(`admin.health.action.${row.action}`, {
              defaultValue: row.action,
            });
            const actorName = row.adminUser?.name || `#${row.adminUser?.id || "?"}`;
            const targetName = row.targetCompany?.name || "—";

            return (
              <Box
                key={row.id}
                className={classes.auditCard}
                style={{ "--i": i }}
              >
                {/* Mobile-only header strip: dot + timestamp accent meta */}
                <Box className={classes.cardHeaderRow}>
                  <span className={`${classes.actionDot} ${dotClassName(row.action)}`} />
                  <Typography className={classes.whenMeta} component="div">
                    {`${formatDateTime(row.createdAt)} — ${
                      row.adminUser?.email || actorName
                    }`}
                  </Typography>
                </Box>

                {/* Desktop dot (col 1) */}
                <span
                  className={`${classes.actionDot} ${dotClassName(row.action)} ${classes.desktopOnly}`}
                />

                {/* Desktop timestamp (col 2) */}
                <Typography className={`${classes.whenMeta} ${classes.desktopOnly}`}>
                  {formatDateTime(row.createdAt)}
                </Typography>

                {/* Desktop actor (col 3) */}
                <Box className={classes.desktopOnly}>
                  <Typography className={classes.actor} component="div">
                    <PersonRoundedIcon />
                    {actorName}
                  </Typography>
                  {row.adminUser?.email && (
                    <Typography className={classes.actorEmail} component="div">
                      {row.adminUser.email}
                    </Typography>
                  )}
                </Box>

                {/* Mobile-only: action description as Roboto medium 14 */}
                <Typography className={classes.actionDescription} component="div">
                  {actionLabel}
                  {row.targetCompany?.name ? ` — ${row.targetCompany.name}` : ""}
                </Typography>

                {/* Desktop action pill (col 4) */}
                <span
                  className={`${classes.actionPill} ${actionClassName(row.action)} ${classes.desktopOnly}`}
                >
                  <Icon />
                  {actionLabel}
                </span>

                {/* Desktop target (col 5) */}
                <Box className={classes.desktopOnly}>
                  <Typography className={classes.target} component="div">
                    <BusinessRoundedIcon />
                    {targetName}
                  </Typography>
                  {row.details?.targetUserEmail && (
                    <Typography className={classes.targetMeta} component="div">
                      → {row.details.targetUserEmail}
                    </Typography>
                  )}
                </Box>

                {/* Desktop IP (col 6) */}
                <Typography className={`${classes.ip} ${classes.desktopOnly}`}>
                  {row.ipAddress || "—"}
                </Typography>

                {/* Diff / context — fgMuted Roboto 12 (visible across breakpoints when present) */}
                {(row.details?.reason ||
                  row.details?.targetUserEmail ||
                  row.ipAddress) && (
                  <Box className={classes.mobileFieldRow}>
                    {row.details?.targetUserEmail && (
                      <>
                        <span className={classes.mobileFieldLabel}>
                          {i18n.t("admin.audit.col.target", { defaultValue: "Target" })}
                        </span>
                        <span className={classes.mobileFieldValue}>
                          {row.details.targetUserEmail}
                        </span>
                      </>
                    )}
                    {row.ipAddress && (
                      <>
                        <span className={classes.mobileFieldLabel}>
                          {i18n.t("admin.audit.col.ip", { defaultValue: "IP" })}
                        </span>
                        <span className={classes.mobileFieldValue}>{row.ipAddress}</span>
                      </>
                    )}
                  </Box>
                )}

                {row.details?.reason && (
                  <Box className={classes.diffBlock}>
                    {`"${row.details.reason}"`}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {pagination && pagination.total > 0 && (
        <Box className={classes.pagination}>
          <span>
            {i18n.t("admin.audit.pagination.showing", {
              start: pagination.start,
              end: pagination.end,
              total: pagination.total,
              defaultValue: `Showing ${pagination.start}–${pagination.end} of ${pagination.total}`,
            })}
          </span>
          <Box className={classes.paginationBtns}>
            <Button
              className={classes.pageBtn}
              variant="outlined"
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {i18n.t("admin.audit.pagination.prev", { defaultValue: "Previous" })}
            </Button>
            <Button
              className={classes.pageBtn}
              variant="outlined"
              size="small"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {i18n.t("admin.audit.pagination.next", { defaultValue: "Next" })}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminAuditLog;
